import CheckmarkIcon from '@ordr-as/icons/CheckmarkIcon';
import ClockIcon from '@ordr-as/icons/ClockIcon';
import type { ExpoOrder } from '../types';
import { expoProgressLabel } from '../selectors';
import CardHeadTime from './CardHeadTime';
import ItemModifiers from './ItemModifiers';
import StationChip from './StationChip';

type Props = {
  order: ExpoOrder;
  stationNameById: Map<number, string>;
  selected: boolean;
  focused: boolean;
  onOpen: () => void;
};

/**
 * The card's one-line verdict. An expo card carries no buttons, so this footer is the only place the
 * order states where it has got to — it is present in every state rather than disappearing when done.
 */
const ProgressNote = ({ order }: { order: ExpoOrder }) => {
  if (order.allDone) {
    return (
      <div className="h-7 w-full flex items-center gap-2 font-semibold text-green-darkest">
        <CheckmarkIcon width={16} height={16} />
        Delivered
      </div>
    );
  }

  return (
    <div className="h-7 w-full flex items-center gap-2 italic text-grey-dark">
      <ClockIcon width={16} height={16} />
      {expoProgressLabel(order)}
    </div>
  );
};

const ExpoOrderCard = ({ order, stationNameById, selected, focused, onOpen }: Props) => {
  const items = order.displayItems;

  const cardClass = selected
    ? 'bg-purple-lightest ring-2 ring-brand-primary'
    : focused
    ? 'bg-brand-white ring-2 ring-brand-primary'
    : 'bg-brand-white';
  const headClass = order.allDone && !selected ? 'bg-green-lightest' : '';
  // Only a finished order gets its products back at full strength; anything still in the kitchen
  // stays greyed, matching the prep boards.
  const productClass = order.allDone ? 'text-brand-black' : 'text-grey-dark';

  return (
    <div
      data-kds-card
      data-card-id={order.groupId}
      className={`kds-card-in rounded flex flex-col overflow-hidden cursor-pointer ${cardClass}`}
      onClick={onOpen}
    >
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-grey-lightest ${headClass}`}>
        <span className="text-small font-semibold text-grey-dark">#{order.orderNumber ?? order.bookingOrderId}</span>
        {order.eatIn && (
          <span className="inline-flex items-center text-extra-small font-semibold whitespace-nowrap px-2 py-0.5 rounded-full bg-grey-lightest text-grey-dark border border-brand-white">
            Eat in
          </span>
        )}
        <CardHeadTime createdUtc={order.createdUtc} />
      </div>

      <div className="px-4 py-3 flex flex-col gap-3 flex-1">
        {items.map((item) => (
          <div key={item.key} className="flex flex-col gap-0.5">
            <div className="flex gap-2 items-baseline">
              <span className={`font-bold min-w-5 ${productClass}`}>{item.quantity}×</span>
              <span className={`font-bold flex-1 ${productClass}`}>{item.name}</span>
            </div>
            <ItemModifiers modifiers={item.modifiers} comment={item.comment} />
            <div className="flex gap-1 flex-wrap pl-5 mt-1">
              {item.chips.map((chip, index) => (
                <StationChip
                  key={index}
                  label={stationNameById.get(chip.stationId) ?? 'Unknown station'}
                  state={chip.state}
                />
              ))}
            </div>
          </div>
        ))}
        {order.orderComment && <div className="text-grey-dark">{order.orderComment}</div>}
      </div>

      <div className="px-4 pb-4 pt-3 flex">
        <ProgressNote order={order} />
      </div>
    </div>
  );
};

export default ExpoOrderCard;
